import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IPeriodOption } from 'app/interfaces/date.interface';
import { DateTimeService } from 'app/services/datetime.service';
import * as moment from 'moment-timezone';
import { ContactService } from 'shared/services/contact/contact.service';

import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from '../../../../app/interfaces/actiontracker.interface';
import { EmotionRateLevel } from '../../../../app/interfaces/moodtracker.interface';
import { ActionTrackerService } from '../../../../app/services/actiontracker.service';
import { ChatService } from '../../../../app/services/chat.service';
import { UserService } from '../../../../app/services/user.service';
import { environment } from '../../../../environments/environment';
import { IContact } from '../../../../shared/interfaces/contact.interface';

@Component({
	selector: 'contact-moodtracker',
	templateUrl: './contact-moodtracker.component.html',
	styleUrls: ['./contact-moodtracker.component.scss'],
})
export class ContactMoodtracker implements OnInit {
	@Input() contact: IContact | null;
	@Input() firstName;
	@Input() professionalId;
	public contactId: null | string = null;
	public moodtrackerReportList = [];
	public periodOptions: IPeriodOption[];
	public periodDates = [];
	public periodValue = null;
	public notFound = false;
	public unauthorized = false;
	public userNotPremium = null;
	public isLoading = true;
	public moodtrackerGoodResults = [];
	public moodtrackerBadResults = [];

	constructor(
		public dateTimeService: DateTimeService,
		public contactService: ContactService,
		public chatService: ChatService,
		public translateService: TranslateService,
		public actionTrackerService: ActionTrackerService,
		public userService: UserService
	) {}

	ngOnInit() {
		this.userService
			.checkSubscriptionAndContactTiers()
			.then(({ hasActiveSubscription }) => {
				if (hasActiveSubscription) {
					this.periodOptions = this.dateTimeService.getPeriodOptions();
					this.periodValue = this.periodOptions[0].value;
					this.periodDates = this.getPeriodDates();
					this.getMoodtrackerData();
				} else {
					this.userNotPremium = true;
				}
			});
	}

	getMoodtrackerData() {
		this.isLoading = true;
		this.contactService
			.getMoodTrackerOfPatient(
				this.contact.refUserId,
				this.periodDates[0],
				this.periodDates[1]
			)
			.toPromise()
			.then((res) => {
				if (res.entriesByDay.length === 0) {
					this.notFound = true;
					return;
				}
				this.moodtrackerReportList = res.entriesByDay;
				this.moodtrackerGoodResults = this.getMoodtrackerResults('good');
				this.moodtrackerBadResults = this.getMoodtrackerResults('bad');
			})
			.catch((err) => {
				this.unauthorized = err.status === 403;
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	getPeriodDates() {
		const today = moment();
		const startDate = moment();
		switch (this.periodValue) {
			case 'week':
				return [
					startDate.subtract(7, 'days').format('yyyy-MM-DD'),
					today.format('yyyy-MM-DD'),
				];
			case 'twoWeeks':
				return [
					startDate.subtract(14, 'days').format('yyyy-MM-DD'),
					today.format('yyyy-MM-DD'),
				];
			case 'month':
				return [
					startDate.subtract(30, 'days').format('yyyy-MM-DD'),
					today.format('yyyy-MM-DD'),
				];
		}
	}

	getMoodtrackerResults(resultType: 'bad' | 'good') {
		let reasons = [];
		this.moodtrackerReportList.forEach((reportByDay) => {
			const entriesByMood = reportByDay.entries.filter((entry) => {
				return resultType === 'good'
					? EmotionRateLevel[entry.emotion.taxonomyId] >= 0
					: EmotionRateLevel[entry.emotion.taxonomyId] <= 0;
			});

			if (entriesByMood.length > 0) {
				entriesByMood.forEach((entry) => {
					reasons = [
						...reasons,
						...(entry.reasons.length > 0 ? entry.reasons : []),
					];
				});
			}
		});

		return [...new Set(reasons.map((reason) => reason.name))];
	}

	onChangePeriod(period) {
		if (period !== this.periodValue) {
			this.periodValue = period;
			this.periodDates = this.getPeriodDates();
			this.getMoodtrackerData();
			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Contact,
				SUBSCOPE_TYPE.Moodtracker,
				ITEM_TYPE.Filter,
				INTERACTION_TYPE.Click,
				{
					contactId: this.contactId,
					professinalId: this.professionalId,
					period,
				}
			);
		}
	}

	reccomendMoodtracker() {
		const message = this.translateService.instant(
			'CONTACT.HUMOR_PAGE.CHAT_RECCOMEND_MOODTRACKER',
			{ contact: this.firstName }
		);
		this.openChat(message);
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.Moodtracker,
			ITEM_TYPE.Reccomend,
			INTERACTION_TYPE.Click,
			{
				contactId: this.contactId,
				professinalId: this.professionalId,
			}
		);
	}

	requestPermission() {
		const message = this.translateService.instant(
			'CONTACT.HUMOR_PAGE.CHAT_REQUEST_PERMISSION',
			{ contact: this.firstName }
		);
		this.openChat(message);
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Contact,
			SUBSCOPE_TYPE.Moodtracker,
			ITEM_TYPE.Permission,
			INTERACTION_TYPE.Click,
			{
				contactId: this.contactId,
				professinalId: this.professionalId,
			}
		);
	}

	openChat(message) {
		this.chatService.startConversation(
			{
				id: this.contact.refUserId,
				email: this.contact.email,
				photoUrl: this.contact.photoUrl,
				username: this.contact.displayName,
				role: this.chatService.role,
			},
			'contact-moodtracker-tab',
			message
		);
	}

	openOfficeUrl(path: string): void {
		window.open(environment.officeUrl + path, '_blank');
	}
}
